import { useEffect, useState, type FormEvent } from 'react';
import { BellRing, Phone, MessageSquare, Trash2, Send, ShieldCheck, ListChecks } from 'lucide-react';
import type {
  CreateRecipientRequest,
  Entitlements,
  NotificationLogEntry,
  NotificationRecipient,
  NotificationSettings,
  NotificationUsage,
  UpdateNotificationSettingsRequest,
} from '@scada-ink/shared/cloud';
import { Button } from '@/components/ui';
import { Alert, SelectField, TextField, Toggle } from '@/components/form';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { cloudApi, errorMessage } from '@/lib/cloudApi';
import { useSession } from '@/lib/session';
import { useAsync } from '@/lib/useAsync';
import { Card, EmptyState, LoadError, Loading, Meter, PortalHeader, Table, formatDateTime } from './components';

const VOICES = [
  { value: 'Polly.Joanna', label: 'Joanna (US English, female)' },
  { value: 'Polly.Matthew', label: 'Matthew (US English, male)' },
  { value: 'Polly.Amy', label: 'Amy (British English, female)' },
  { value: 'Polly.Lupe', label: 'Lupe (US Spanish, female)' },
  { value: 'Polly.Lea', label: 'Léa (French, female)' },
  { value: 'Polly.Vicki', label: 'Vicki (German, female)' },
  { value: 'alice', label: 'Alice (Twilio standard)' },
];

const LANGUAGES = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es-US', label: 'Spanish (US)' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
];

const E164 = /^\+[1-9]\d{6,14}$/;

function SettingsForm({ settings, onSaved, canEdit }: { settings: NotificationSettings; onSaved: (s: NotificationSettings) => void; canEdit: boolean }) {
  const [form, setForm] = useState(settings);
  const [token, setToken] = useState('');
  const [clearToken, setClearToken] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => setForm(settings), [settings]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    const body: UpdateNotificationSettingsRequest = {
      mode: form.mode,
      voice: form.voice,
      language: form.language,
      allowlistEnforced: form.allowlistEnforced,
      monthlySmsCap: form.monthlySmsCap,
      monthlyVoiceCap: form.monthlyVoiceCap,
    };
    if (form.mode === 'byo') {
      body.twilioAccountSid = form.twilioAccountSid?.trim() || null;
      body.fromNumber = form.fromNumber?.trim() || null;
      if (token) body.twilioAuthToken = token;
      else if (clearToken) body.twilioAuthToken = '';
    }
    try {
      const saved = await cloudApi.put<NotificationSettings>('/api/portal/notifications/settings', body);
      onSaved(saved);
      setToken('');
      setClearToken(false);
      setNotice({ kind: 'success', text: 'Settings saved.' });
    } catch (err) {
      setNotice({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  const capValue = (v: number | null) => (v === null ? '' : String(v));
  const parseCap = (v: string) => (v.trim() === '' ? null : Math.max(0, Math.floor(Number(v)) || 0));

  return (
    <form onSubmit={save} className="space-y-6">
      <fieldset disabled={!canEdit || busy} className="space-y-6">
        <div>
          <div className="text-xs font-semibold mb-2">Twilio account</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { id: 'managed', title: 'Managed by Scada.Ink', body: 'Our Twilio number; usage counts against your plan allowance.' },
              { id: 'byo', title: 'Bring your own Twilio', body: 'Your Twilio account and caller ID; Twilio bills you directly.' },
            ].map((m) => (
              <label
                key={m.id}
                className={`cursor-pointer rounded-[var(--radius-glass-sm)] border p-3.5 transition-all ${
                  form.mode === m.id ? 'border-brand-primary bg-brand-primary/5 ring-4 ring-brand-primary/10' : 'border-glass-border hover:border-glass-border-bright'
                }`}
              >
                <input type="radio" className="sr-only" name="mode" checked={form.mode === m.id} onChange={() => setForm({ ...form, mode: m.id as NotificationSettings['mode'] })} />
                <span className="block text-sm font-semibold">{m.title}</span>
                <span className="block text-xs text-text-secondary mt-0.5">{m.body}</span>
              </label>
            ))}
          </div>
        </div>

        {form.mode === 'byo' && (
          <div className="grid sm:grid-cols-2 gap-4 rounded-[var(--radius-glass-sm)] bg-ink-muted/60 border border-glass-border p-4">
            <TextField
              label="Account SID"
              className="font-mono"
              placeholder="AC…"
              value={form.twilioAccountSid ?? ''}
              onChange={(e) => setForm({ ...form, twilioAccountSid: e.target.value })}
              pattern="AC[0-9a-fA-F]{32}"
              title="AC followed by 32 hexadecimal characters"
            />
            <TextField
              label="Auth token"
              type="password"
              autoComplete="off"
              placeholder={settings.hasAuthToken && !clearToken ? '•••••••• (stored)' : 'Paste your auth token'}
              value={token}
              onChange={(e) => setToken(e.target.value)}
              hint={
                settings.hasAuthToken ? (
                  <label className="inline-flex items-center gap-1.5">
                    <input type="checkbox" checked={clearToken} onChange={(e) => setClearToken(e.target.checked)} /> Remove the stored token
                  </label>
                ) : (
                  'Stored encrypted; never shown again.'
                )
              }
            />
            <TextField
              label="From number"
              className="font-mono"
              placeholder="+15551234567"
              value={form.fromNumber ?? ''}
              onChange={(e) => setForm({ ...form, fromNumber: e.target.value })}
              hint="A Twilio number on your account, in +E.164 format."
            />
            <div className="sm:col-span-2 text-xs text-text-secondary space-y-1.5">
              <div className="font-semibold text-text-primary">Replies to alarm texts</div>
              {settings.inboundSmsWebhookUrl ? (
                <>
                  <p>
                    So people can acknowledge an alarm by replying to its text (&ldquo;ACK K7P&rdquo;), set this as the{' '}
                    <span className="font-semibold">A message comes in</span> webhook (HTTP POST) on your from-number in the Twilio console:
                  </p>
                  <code className="block font-mono text-[11px] break-all rounded bg-white/70 border border-glass-border px-2 py-1.5 select-all">
                    {settings.inboundSmsWebhookUrl}
                  </code>
                  <p>Keypad presses on alarm calls need nothing set up: each call tells Twilio where to send them.</p>
                </>
              ) : (
                <p>Reply acknowledgement isn&rsquo;t available from this service yet: it doesn&rsquo;t know its public address.</p>
              )}
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <SelectField label="Voice" value={form.voice} onChange={(e) => setForm({ ...form, voice: e.target.value })} options={VOICES.some((v) => v.value === form.voice) ? VOICES : [{ value: form.voice, label: form.voice }, ...VOICES]} />
          <SelectField label="Voice language" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} options={LANGUAGES.some((l) => l.value === form.language) ? LANGUAGES : [{ value: form.language, label: form.language }, ...LANGUAGES]} />
          <TextField label="Monthly SMS cap" type="number" min={0} placeholder="Plan allowance" value={capValue(form.monthlySmsCap)} onChange={(e) => setForm({ ...form, monthlySmsCap: parseCap(e.target.value) })} hint="Leave blank to use the plan allowance." />
          <TextField label="Monthly voice-minute cap" type="number" min={0} placeholder="Plan allowance" value={capValue(form.monthlyVoiceCap)} onChange={(e) => setForm({ ...form, monthlyVoiceCap: parseCap(e.target.value) })} />
        </div>

        <Toggle
          label="Only send to numbers on the recipient list"
          description="Recommended. A misconfigured alarm pipeline cannot text or call a number you have not approved."
          checked={form.allowlistEnforced}
          onChange={(v) => setForm({ ...form, allowlistEnforced: v })}
        />
      </fieldset>

      {notice && <Alert kind={notice.kind}>{notice.text}</Alert>}
      {canEdit ? (
        <div className="flex justify-end">
          <Button type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-text-muted">Only the account owner can change these settings.</p>
      )}
    </form>
  );
}

function Recipients({ recipients, onChange }: { recipients: NotificationRecipient[]; onChange: () => void }) {
  const [form, setForm] = useState<CreateRecipientRequest>({ label: '', phone: '', sms: true, voice: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add(e: FormEvent) {
    e.preventDefault();
    const phone = form.phone.replace(/[\s()-]/g, '');
    if (!E164.test(phone)) {
      setError('Enter the number in international format, e.g. +15551234567.');
      return;
    }
    if (!form.sms && !form.voice) {
      setError('Allow at least SMS or voice for this recipient.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await cloudApi.post('/api/portal/notifications/recipients', { ...form, phone });
      setForm({ label: '', phone: '', sms: true, voice: true });
      onChange();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(r: NotificationRecipient) {
    if (!window.confirm(`Remove ${r.label} (${r.phone})?`)) return;
    try {
      await cloudApi.del(`/api/portal/notifications/recipients/${encodeURIComponent(r.id)}`);
      onChange();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <>
      {recipients.length === 0 ? (
        <p className="text-sm text-text-muted mb-4">No recipients yet. With the allow-list on, nothing can be sent until you add one.</p>
      ) : (
        <ul className="divide-y divide-glass-border mb-4 -mt-2">
          {recipients.map((r) => (
            <li key={r.id} className="flex items-center gap-3 py-2.5">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{r.label}</div>
                <div className="text-xs font-mono text-text-muted">{r.phone}</div>
              </div>
              <span className="flex gap-1">
                {r.sms && <MessageSquare size={14} className="text-brand-primary" aria-label="SMS" />}
                {r.voice && <Phone size={14} className="text-brand-secondary" aria-label="Voice" />}
              </span>
              <button type="button" onClick={() => void remove(r)} className="p-1.5 text-text-muted hover:text-status-fault" aria-label={`Remove ${r.label}`}>
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={add} className="space-y-3 pt-4 border-t border-glass-border">
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Name" required placeholder="Night shift lead" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          <TextField label="Phone" required type="tel" placeholder="+15551234567" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex gap-4 text-sm">
            <label className="inline-flex items-center gap-1.5">
              <input type="checkbox" checked={form.sms} onChange={(e) => setForm({ ...form, sms: e.target.checked })} /> SMS
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input type="checkbox" checked={form.voice} onChange={(e) => setForm({ ...form, voice: e.target.checked })} /> Voice
            </label>
          </div>
          <Button type="submit" size="sm" disabled={busy}>
            Add recipient
          </Button>
        </div>
        {error && <Alert kind="error">{error}</Alert>}
      </form>
    </>
  );
}

function TestSend({ recipients, onSent }: { recipients: NotificationRecipient[]; onSent: () => void }) {
  const [channel, setChannel] = useState<'sms' | 'voice'>('sms');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  async function send(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      await cloudApi.post('/api/portal/notifications/test', { channel, to: to.replace(/[\s()-]/g, '') });
      setNotice({ kind: 'success', text: channel === 'sms' ? 'Test message sent.' : 'Test call placed.' });
      onSent();
    } catch (err) {
      setNotice({ kind: 'error', text: errorMessage(err) });
      onSent();
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={send} className="space-y-3">
      <div className="grid grid-cols-[auto_1fr] gap-3 items-end">
        <SelectField
          label="Channel"
          value={channel}
          onChange={(e) => setChannel(e.target.value as 'sms' | 'voice')}
          options={[
            { value: 'sms', label: 'SMS' },
            { value: 'voice', label: 'Voice call' },
          ]}
        />
        <label className="block">
          <span className="block text-xs font-semibold mb-1.5">To</span>
          <input
            list="recipient-numbers"
            required
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="+15551234567"
            className="w-full px-3.5 py-2.5 rounded-[var(--radius-glass-sm)] bg-white border border-glass-border-bright text-sm font-mono outline-none focus:border-brand-primary focus:ring-4 focus:ring-brand-primary/10"
          />
          <datalist id="recipient-numbers">
            {recipients.map((r) => (
              <option key={r.id} value={r.phone}>
                {r.label}
              </option>
            ))}
          </datalist>
        </label>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-text-muted">Counts toward this month’s usage.</span>
        <Button type="submit" size="sm" variant="secondary" disabled={busy}>
          <Send size={13} /> {busy ? 'Sending…' : 'Send test'}
        </Button>
      </div>
      {notice && <Alert kind={notice.kind}>{notice.text}</Alert>}
    </form>
  );
}

export function NotificationsPage() {
  usePageTitle('SMS & Voice');
  const { session } = useSession();
  const { data, error, loading, reload, setData } = useAsync(async () => {
    const [settings, recipients, usage, log, entitlements] = await Promise.all([
      cloudApi.get<NotificationSettings>('/api/portal/notifications/settings'),
      cloudApi.get<NotificationRecipient[]>('/api/portal/notifications/recipients'),
      cloudApi.get<NotificationUsage>('/api/portal/notifications/usage'),
      cloudApi.get<NotificationLogEntry[]>('/api/portal/notifications/log'),
      cloudApi.get<Entitlements>('/api/portal/entitlements'),
    ]);
    return { settings, recipients, usage, log, entitlements };
  });

  if (loading && !data) return <Loading />;
  if (error || !data) return <LoadError message={error ?? 'Could not load notification settings.'} onRetry={reload} />;

  const { settings, recipients, usage, log, entitlements } = data;
  const active = entitlements.notifications.active;

  return (
    <>
      <PortalHeader
        title="SMS & Voice Alerts"
        description="Alarm pipelines on your appliances send texts and place calls through Scada.Ink Cloud and Twilio. Configure where messages come from, who may receive them and how many can be sent."
        actions={
          !active && (
            <Button to="/portal/billing?plan=notify-standard" size="sm">
              <BellRing size={14} /> Subscribe to Alerts
            </Button>
          )
        }
      />

      {!active && (
        <div className="mb-6">
          <Alert kind="info">
            No alerts plan is active, so appliances cannot send SMS or voice alerts yet. You can still configure settings and recipients now.
          </Alert>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="glass-card p-5">
          <Meter label="SMS this month" used={usage.sms.used} max={settings.mode === 'byo' ? usage.sms.cap ?? 0 : Math.min(usage.sms.included, usage.sms.cap ?? Infinity)} />
          <div className="mt-2 text-xs text-text-muted">
            {settings.mode === 'byo' ? 'Billed by your Twilio account' : `${usage.sms.included.toLocaleString()} included`}
            {usage.sms.cap !== null && ` · cap ${usage.sms.cap.toLocaleString()}`}
          </div>
        </div>
        <div className="glass-card p-5">
          <Meter label="Voice minutes this month" used={usage.voice.used} max={settings.mode === 'byo' ? usage.voice.cap ?? 0 : Math.min(usage.voice.included, usage.voice.cap ?? Infinity)} unit="min" />
          <div className="mt-2 text-xs text-text-muted">
            {settings.mode === 'byo' ? 'Billed by your Twilio account' : `${usage.voice.included.toLocaleString()} included`}
            {usage.voice.cap !== null && ` · cap ${usage.voice.cap.toLocaleString()}`}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-6 mb-6">
        <Card title="Settings">
          <SettingsForm settings={settings} canEdit={session?.user.role === 'owner'} onSaved={(s) => setData({ ...data, settings: s })} />
        </Card>
        <div className="space-y-6">
          <Card
            title={
              <span className="inline-flex items-center gap-2">
                <ShieldCheck size={15} className="text-brand-primary" /> Recipients
              </span>
            }
          >
            <Recipients recipients={recipients} onChange={() => void reload()} />
          </Card>
          <Card title="Send a test">
            <TestSend recipients={recipients} onSent={() => void reload()} />
          </Card>
        </div>
      </div>

      <Card title="Delivery log" padded={log.length === 0}>
        {log.length === 0 ? (
          <EmptyState icon={ListChecks} title="Nothing sent yet" body="Every SMS and call — sent, failed or blocked — is recorded here." />
        ) : (
          <Table head={['Time', 'Channel', 'To', 'Status', 'Source', 'Units']}>
            {log.map((entry) => (
              <tr key={entry.id}>
                <td className="px-5 py-2.5 whitespace-nowrap text-text-secondary">{formatDateTime(entry.at)}</td>
                <td className="px-5 py-2.5">{entry.channel === 'sms' ? 'SMS' : 'Voice'}</td>
                <td className="px-5 py-2.5 font-mono text-xs">{entry.to}</td>
                <td className="px-5 py-2.5">
                  <span
                    className={`text-xs font-semibold ${entry.status === 'sent' ? 'text-status-running' : entry.status === 'blocked' ? 'text-status-idle' : 'text-status-fault'}`}
                  >
                    {entry.status}
                  </span>
                  {entry.error && <div className="text-xs text-text-muted max-w-xs truncate" title={entry.error}>{entry.error}</div>}
                </td>
                <td className="px-5 py-2.5 text-text-secondary">{entry.source === 'portal-test' ? 'Portal test' : entry.applianceName ?? 'Appliance'}</td>
                <td className="px-5 py-2.5 tabular-nums">{entry.units}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}
